import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import DatePickerField from '../common/DatePickerField';
import { blockBackdropClose, FormDialogTitle, useErrorToast } from '../common';
import { normalizeHttpUrl } from '../common/safeUrl';
import { EVENT_TYPE_META, type EventType } from './eventType';
import {
  eventService,
  type EventCreateRequest,
  type EventSummary,
} from '../../services/eventService';

type EventCreateDialogProps = Readonly<{
  open: boolean;
  onClose: () => void;
  onCreated: (created: EventSummary) => void;
}>;

type FormState = {
  title: string;
  city: string;
  location: string;
  hostName: string;
  eventDate: string;
  type: EventType;
  registrationStartDate: string;
  registrationEndDate: string;
  registerUrl: string;
  isPublished: boolean;
  collectBirth: boolean;
  collectGender: boolean;
};

const EMPTY_FORM: FormState = {
  title: '',
  city: '',
  location: '',
  hostName: '',
  eventDate: '',
  type: 'MARATHON',
  registrationStartDate: '',
  registrationEndDate: '',
  registerUrl: '',
  isPublished: false,
  collectBirth: true,
  collectGender: true,
};

type FieldProps = Readonly<{
  label: string;
  required?: boolean;
  children: React.ReactNode;
}>;

function Field({ label, required, children }: FieldProps) {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ fontSize: 12.5, fontWeight: 700, color: '#475569', mb: 0.75 }}>
        {label}
        {required && <Box component="span" sx={{ color: '#dc2626', ml: 0.5 }}>*</Box>}
      </Box>
      {children}
    </Box>
  );
}

function EventCreateDialog({ open, onClose, onCreated }: EventCreateDialogProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showError, errorToast } = useErrorToast();

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const resetAndClose = () => {
    setForm(EMPTY_FORM);
    setImageFile(null);
    setImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    onClose();
  };

  const handlePickImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const canSubmit =
    !!form.title.trim() &&
    !!form.city.trim() &&
    !!form.location.trim() &&
    !!form.hostName.trim() &&
    !!form.eventDate &&
    !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const payload: EventCreateRequest = {
        title: form.title.trim(),
        city: form.city.trim(),
        location: form.location.trim(),
        hostName: form.hostName.trim(),
        eventDate: form.eventDate,
        type: form.type,
        registrationStartDate: form.registrationStartDate || undefined,
        registrationEndDate: form.registrationEndDate || undefined,
        registerUrl: normalizeHttpUrl(form.registerUrl) || undefined,
        isPublished: form.isPublished,
        collectBirth: form.collectBirth,
        collectGender: form.collectGender,
      };
      const created = await eventService.create(payload, imageFile);
      onCreated(created);
      resetAndClose();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputSx = { '& .MuiInputBase-input': { fontSize: 14 } };

  return (
    <Dialog open={open} onClose={blockBackdropClose(resetAndClose)} maxWidth="sm" fullWidth>
      <FormDialogTitle onClose={resetAndClose} sx={{ fontWeight: 800, fontSize: 18 }}>
        대회 추가
      </FormDialogTitle>
      <DialogContent dividers>
        {/* 포스터 업로드 (선택) — 상단 전체폭 */}
        <Field label="포스터">
          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              width: '100%',
              height: 150,
              borderRadius: 2,
              border: '1px dashed #cbd5e1',
              backgroundColor: '#f8fafc',
              backgroundImage: imagePreview ? `url(${imagePreview})` : 'none',
              backgroundSize: imagePreview ? 'contain' : 'auto',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#94a3b8',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              textAlign: 'center',
              px: 1,
              '&:hover': { borderColor: '#1da1f2', color: '#1da1f2' },
            }}
          >
            {!imagePreview && '클릭하여 이미지 선택'}
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handlePickImage}
          />
        </Field>

        <Field label="대회명" required>
          <TextField
            value={form.title}
            onChange={(e) => setField('title', e.target.value)}
            size="small"
            fullWidth
            placeholder="예: OOO 마라톤 대회"
            sx={inputSx}
          />
        </Field>
        <Field label="대회 일시" required>
          <DatePickerField
            value={form.eventDate}
            onChange={(v) => setField('eventDate', v)}
            placeholder="날짜 선택"
            fullWidth
          />
        </Field>
        <Field label="분류" required>
          <Select
            value={form.type}
            onChange={(e) => setField('type', e.target.value as EventType)}
            size="small"
            fullWidth
            sx={{ fontSize: 14 }}
          >
            {(Object.keys(EVENT_TYPE_META) as EventType[]).map((type) => (
              <MenuItem key={type} value={type} sx={{ fontSize: 14 }}>
                {EVENT_TYPE_META[type].label}
              </MenuItem>
            ))}
          </Select>
        </Field>
        <Field label="대회 도시" required>
          <TextField
            value={form.city}
            onChange={(e) => setField('city', e.target.value)}
            size="small"
            fullWidth
            placeholder="예: 서울특별시"
            sx={inputSx}
          />
        </Field>
        <Field label="대회 장소" required>
          <TextField
            value={form.location}
            onChange={(e) => setField('location', e.target.value)}
            size="small"
            fullWidth
            placeholder="예: 잠실종합운동장"
            sx={inputSx}
          />
        </Field>
        <Field label="주최사" required>
          <TextField
            value={form.hostName}
            onChange={(e) => setField('hostName', e.target.value)}
            size="small"
            fullWidth
            placeholder="예: 서울 시청"
            sx={inputSx}
          />
        </Field>
        <Field label="접수 시작일">
          <DatePickerField
            value={form.registrationStartDate}
            onChange={(v) => setField('registrationStartDate', v)}
            placeholder="날짜 선택"
            fullWidth
          />
        </Field>
        <Field label="접수 마감일">
          <DatePickerField
            value={form.registrationEndDate}
            onChange={(v) => setField('registrationEndDate', v)}
            placeholder="날짜 선택"
            fullWidth
          />
        </Field>
        <Field label="참가 등록 링크">
          <TextField
            value={form.registerUrl}
            onChange={(e) => setField('registerUrl', e.target.value)}
            onBlur={() => setField('registerUrl', normalizeHttpUrl(form.registerUrl))}
            size="small"
            fullWidth
            placeholder="https://"
            sx={inputSx}
          />
        </Field>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Switch
            checked={form.isPublished}
            onChange={(e) => setField('isPublished', e.target.checked)}
            size="small"
          />
          <Box sx={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
            홈페이지 즉시 노출
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Tooltip title="수집 시 참가자 등록에서 생년월일이 필수가 되고 연령대가 계산됩니다.">
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <Switch
                checked={form.collectBirth}
                onChange={(e) => setField('collectBirth', e.target.checked)}
                size="small"
              />
            </Box>
          </Tooltip>
          <Box sx={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
            생년월일 수집
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Tooltip title="수집 시 참가자 등록에서 성별이 필수가 됩니다.">
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <Switch
                checked={form.collectGender}
                onChange={(e) => setField('collectGender', e.target.checked)}
                size="small"
              />
            </Box>
          </Tooltip>
          <Box sx={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
            성별 수집
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={resetAndClose}
          disabled={isSubmitting}
          sx={{ color: '#8a94a6', fontWeight: 700 }}
        >
          취소
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{
            backgroundColor: '#1da1f2',
            fontWeight: 700,
            '&:hover': { backgroundColor: '#1791db' },
          }}
        >
          {isSubmitting ? '추가 중…' : '추가'}
        </Button>
      </DialogActions>
      {errorToast}
    </Dialog>
  );
}

export default EventCreateDialog;
